import { useMemo, useState } from 'react';
import { Box, Button, Chip, Grid, Stack, TextField, Typography } from '@mui/material';
import { Place } from '@mui/icons-material';
import MapPinPicker from '../common/MapPinPicker';
import placesService from '../../services/placesService';
import { featuresFor } from './FEATURE_OPTIONS';
import { text } from '../../theme/colors';

// Spec §2: these facts are what a buyer filters by. They gate submission but
// never verification, so say so plainly — a realtor who thinks editing them
// costs a re-verification simply will not fill them in.
const HOUSE_HINT = 'Buyers filter by these. Bedrooms, bathrooms and floor area are required before you can submit; they never affect verification.';
const STAND_HINT = 'The map pin is required before you can submit. It places the stand on the buyer\'s map; the exact point is only shown after someone enquires.';

// The server caps the feature list at 30 and silently drops the rest; stop at
// the same number so the realtor never loses an entry they thought they saved.
const FEATURE_MAX = 30;

// Mirror ListingService.hasPin: a blank box is "no pin", not zero, and 0,0 is
// the Atlantic — the shape a half-filled location jsonb takes, never a stand.
const coord = (v) => (v === '' || v === null || v === undefined ? NaN : Number(v));
const pinOf = (loc) => {
  const lat = coord(loc?.lat); const lng = coord(loc?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
};

/**
 * HomeDetailsFields — the "Home details" (house) / "Stand details" (stand)
 * section of the realtor listing editor.
 *
 * Props:
 *   listingType - 'house' | 'stand'; houses get the room/size fields
 *   value       - the editor's form object (bedrooms, bathrooms, floorAreaSqm,
 *                 yearBuilt, parking, features[], location{suburb,city,lat,lng})
 *   onChange    - called with the whole next form object
 *   disabled    - true when the listing's status does not allow detail edits
 */
/** `sections` picks the parts to show: 'facts' (rooms, features) and/or 'pin' (the map pin). */
export default function HomeDetailsFields({ listingType, value, onChange, disabled, sections = ['facts', 'pin'] }) {
  const showFacts = sections.includes('facts');
  const showPin = sections.includes('pin');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerCenter, setPickerCenter] = useState(null);
  const [custom, setCustom] = useState('');

  const isHouse = listingType === 'house';
  const loc = value.location || {};
  const features = value.features || [];
  // One object identity per pin so MapPinPicker's open effect does not rebuild
  // the map on every keystroke elsewhere in the form.
  const pin = useMemo(() => pinOf({ lat: loc.lat, lng: loc.lng }), [loc.lat, loc.lng]);
  const hasPin = !!pin;

  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const setFeatures = (next) => onChange({ ...value, features: next });
  const toggle = (f) => setFeatures(features.some((x) => x.toLowerCase() === f.toLowerCase())
    ? features.filter((x) => x.toLowerCase() !== f.toLowerCase())
    : [...features, f]);
  const addCustom = () => {
    const v = custom.trim();
    if (!v) return;
    if (!features.some((x) => x.toLowerCase() === v.toLowerCase())) setFeatures([...features, v]);
    setCustom('');
  };

  // MapPinPicker takes coordinates, not an address, so resolve the suburb/city
  // to a centre first. A failed geocode is not an error: the picker falls back
  // to the centre of Zimbabwe and the realtor pans from there.
  const openPicker = async () => {
    setPickerOpen(true);
    if (hasPin || pickerCenter) return;
    const where = [loc.suburb, loc.city, 'Zimbabwe'].filter(Boolean).join(', ');
    try {
      const r = await placesService.geocodeAddress(where);
      if (r?.data?.coordinates) setPickerCenter(r.data.coordinates);
    } catch { /* keep the default centre */ }
  };

  const chips = featuresFor(listingType);
  const extras = features.filter((f) => !chips.some((c) => c.toLowerCase() === f.toLowerCase()));

  return (
    <Box>
      {showFacts && showPin && <Typography variant="body2" sx={{ color: text.secondary, mb: 2 }}>{isHouse ? HOUSE_HINT : STAND_HINT}</Typography>}
      <Grid container spacing={2}>
        {showFacts && isHouse && (
          <>
            <Grid item xs={6} md={2}><TextField fullWidth type="number" label="Bedrooms" value={value.bedrooms ?? ''} onChange={set('bedrooms')} disabled={disabled} inputProps={{ min: 1, max: 30 }} /></Grid>
            <Grid item xs={6} md={2}><TextField fullWidth type="number" label="Bathrooms" value={value.bathrooms ?? ''} onChange={set('bathrooms')} disabled={disabled} inputProps={{ min: 1, max: 30, step: 0.5 }} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth type="number" label="Floor area (m²)" value={value.floorAreaSqm ?? ''} onChange={set('floorAreaSqm')} disabled={disabled} inputProps={{ min: 1, max: 100000 }} /></Grid>
            <Grid item xs={6} md={2}><TextField fullWidth type="number" label="Year built" value={value.yearBuilt ?? ''} onChange={set('yearBuilt')} disabled={disabled} inputProps={{ min: 1850, max: new Date().getFullYear() + 1 }} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth label="Parking" value={value.parking ?? ''} onChange={set('parking')} disabled={disabled} placeholder="Double lock-up garage" inputProps={{ maxLength: 64 }} /></Grid>
          </>
        )}

        {showFacts && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Features</Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {chips.map((f) => {
              const on = features.some((x) => x.toLowerCase() === f.toLowerCase());
              return (
                <Chip
                  key={f}
                  label={f}
                  role="button"
                  aria-pressed={on}
                  clickable={!disabled}
                  disabled={disabled}
                  color={on ? 'primary' : 'default'}
                  variant={on ? 'filled' : 'outlined'}
                  onClick={disabled ? undefined : () => toggle(f)}
                />
              );
            })}
            {extras.map((f) => (
              <Chip key={f} label={f} role="button" aria-pressed color="primary" disabled={disabled} onDelete={disabled ? undefined : () => toggle(f)} onClick={disabled ? undefined : () => toggle(f)} />
            ))}
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center">
            <TextField
              size="small" label="Add feature" value={custom} disabled={disabled}
              onChange={(e) => setCustom(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
              inputProps={{ maxLength: 40 }}
            />
            <Button onClick={addCustom} disabled={disabled || !custom.trim() || features.length >= FEATURE_MAX}>Add</Button>
          </Stack>
        </Grid>
        )}

        {showPin && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Map pin</Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
            <Button variant="outlined" startIcon={<Place />} aria-label={hasPin ? 'Move the map pin' : 'Set the map pin'} onClick={openPicker} disabled={disabled}>
              {hasPin ? 'Move the map pin' : 'Set the map pin'}
            </Button>
            {hasPin ? (
              <Typography variant="body2" sx={{ color: text.secondary }}>{`Pin: ${pin.lat.toFixed(5)}, ${pin.lng.toFixed(5)}`}</Typography>
            ) : (
              <Typography variant="body2" sx={{ color: text.tertiary }}>No pin yet — buyers cannot see this on the map until you set one.</Typography>
            )}
          </Stack>
        </Grid>
        )}
      </Grid>

      <MapPinPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={(pin) => { onChange({ ...value, location: { ...loc, lat: pin.lat, lng: pin.lng } }); setPickerOpen(false); }}
        initialPin={pin}
        initialCenter={pickerCenter}
        addressLabel={[loc.suburb, loc.city].filter(Boolean).join(', ')}
      />
    </Box>
  );
}
